/**
 * Predator Identifier Tool - "What Killed My Animal?"
 *
 * A multi-step wizard that helps farmers identify predators through:
 * 1. Multi-photo upload with guided prompts for different evidence types
 * 2. Evidence checklist
 * 3. AI-powered identification with confidence scores
 * 4. Prevention recommendations with term tooltips
 */
import React, { useState, useEffect, useRef } from 'react';
import { bridgeClubAPI } from '../../services/bridgeClubAPI';
import PreventionTip from './PreventionTip';
import './PredatorIdentifier.css';

// Types
interface ChecklistCategory {
  label: string;
  options: Array<{ code: string; label: string }>;
}

interface Checklist {
  [key: string]: ChecklistCategory;
}

interface PreventionTerm {
  term: string;
  definition: string;
  also_called?: string[];
}

interface PreventionItem {
  tip: string;
  term?: string;
}

// Photo types with guided prompts
interface PhotoType {
  id: string;
  label: string;
  instruction: string;
  icon: string;
}

const PHOTO_TYPES: PhotoType[] = [
  { id: 'carcass', label: 'Carcass/Remains', instruction: 'Show the overall condition of the carcass. What parts are missing or eaten?', icon: '🦴' },
  { id: 'wounds', label: 'Wound Detail', instruction: 'Take a close-up of any visible wounds. Try to show the size and type of injuries.', icon: '🔍' },
  { id: 'tracks', label: 'Tracks/Prints', instruction: 'Photograph any paw prints. Place an object nearby for size reference.', icon: '🐾' },
  { id: 'scene', label: 'Overall Scene', instruction: 'Take a wide shot showing where the attack happened.', icon: '📍' },
  { id: 'feathers', label: 'Feathers/Fur', instruction: 'Show any scattered feathers or fur. This helps identify how the predator killed.', icon: '🪶' },
  { id: 'fencing', label: 'Entry Point', instruction: 'If you found where the predator entered, photograph any damage or holes.', icon: '🚧' },
];

interface UploadedPhoto {
  id: string;
  photo_type: string;
  image_base64: string;
  filename?: string;
}

interface PredatorResult {
  predator_type: string;
  name: string;
  combined_score: number;
  evidence_score: number;
  photo_confidence: number;
  reasoning: string;
  evidence_supporting?: string[];
  prevention: PreventionItem[];
  legal_note: string;
  typical_losses: string;
  carcass_condition: string;
  category: string;
  image_url?: string;
  description?: string;
}

interface IdentificationResult {
  combined_results: PredatorResult[];
  primary_suspect: PredatorResult | null;
  confidence_level: string;
  photos_analyzed?: number;
  photo_analysis?: {
    wounds_observed: Array<{ location: string; type: string; description: string }>;
    likely_predators: Array<{ predator: string; confidence: string; reasoning: string }>;
    photo_quality_notes?: string;
    per_photo_observations?: Array<{
      photo_number: number;
      photo_type: string;
      key_observations: string[];
      predator_clues: string[];
    }>;
    confidence_factors?: {
      photo_quality: string;
      evidence_clarity: string;
      multiple_photo_agreement: boolean;
    };
  };
}

const LIVESTOCK_TYPES = [
  { id: 'chickens', label: 'Chickens/Poultry' },
  { id: 'ducks', label: 'Ducks' },
  { id: 'turkeys', label: 'Turkeys' },
  { id: 'geese', label: 'Geese' },
  { id: 'quail', label: 'Quail' },
  { id: 'rabbits', label: 'Rabbits' },
  { id: 'sheep', label: 'Sheep' },
  { id: 'goats', label: 'Goats' },
  { id: 'pigs', label: 'Pigs' },
  { id: 'calves', label: 'Calves' },
  { id: 'cats', label: 'Cats' },
  { id: 'small_dogs', label: 'Small Dogs' },
];

interface AnimalBatch {
  id: number;
  species: string;
  custom_name?: string;
  count: number;
}

interface FarmZone {
  id: number;
  name: string;
}

interface PredatorIdentifierProps {
  onClose?: () => void;
  isModal?: boolean;
  isWholesale?: boolean;
  batches?: AnimalBatch[];
  zones?: FarmZone[];
}

const PredatorIdentifier: React.FC<PredatorIdentifierProps> = ({
  onClose,
  isModal = false,
  isWholesale = false,
  batches = [],
  zones = [],
}) => {
  // Wizard state
  const [step, setStep] = useState(1);
  const [livestockType, setLivestockType] = useState('');
  const [selectedEvidence, setSelectedEvidence] = useState<string[]>([]);
  const [photos, setPhotos] = useState<UploadedPhoto[]>([]);
  const [activePhotoType, setActivePhotoType] = useState<string | null>(null);
  const [additionalContext, setAdditionalContext] = useState('');
  const [countLost, setCountLost] = useState(1);

  // Wholesale-specific state
  const [selectedBatchId, setSelectedBatchId] = useState<number | null>(null);
  const [selectedZoneId, setSelectedZoneId] = useState<number | null>(null);

  // Data state
  const [checklist, setChecklist] = useState<Checklist | null>(null);
  const [preventionTerms, setPreventionTerms] = useState<Record<string, PreventionTerm>>({});
  const [result, setResult] = useState<IdentificationResult | null>(null);

  // Loading/error states
  const [loading, setLoading] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  // Expanded alternatives state for accordion
  const [expandedAlternatives, setExpandedAlternatives] = useState<Set<number>>(new Set());
  const [showPhotoDetails, setShowPhotoDetails] = useState(false);

  const fileInputRef = useRef<HTMLInputElement>(null);

  // Toggle expanded state for alternative predators
  const toggleAlternativeExpanded = (index: number) => {
    setExpandedAlternatives(prev => {
      const next = new Set(prev);
      if (next.has(index)) {
        next.delete(index);
      } else {
        next.add(index);
      }
      return next;
    });
  };

  // Get qualitative match strength label
  const getMatchStrength = (score: number, isTopResult: boolean): { label: string; className: string } => {
    if (isTopResult) {
      return { label: 'Best Match', className: 'match-best' };
    }
    if (score >= 80) {
      return { label: 'Strong match', className: 'match-strong' };
    }
    if (score >= 60) {
      return { label: 'Possible match', className: 'match-possible' };
    }
    return { label: 'Less likely', className: 'match-unlikely' };
  };

  // Get evidence match description
  const getEvidenceMatchDescription = (score: number): string => {
    const evidenceCount = selectedEvidence.length;
    const matchingPoints = Math.round((score / 100) * evidenceCount);
    if (evidenceCount === 0) return 'Photo analysis only';
    return `${matchingPoints} of ${evidenceCount} evidence points match`;
  };

  // Load checklist and prevention terms on mount
  useEffect(() => {
    loadInitialData();
  }, []);

  const loadInitialData = async () => {
    try {
      setLoading(true);
      const [checklistData, termsData] = await Promise.all([
        bridgeClubAPI.getEvidenceChecklist(),
        bridgeClubAPI.getPreventionTerms(),
      ]);
      setChecklist(checklistData.checklist);
      setPreventionTerms(termsData.terms);
    } catch (err) {
      console.error('Failed to load predator ID data:', err);
      setError('Failed to load tool data. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Photo handling - multi-photo support
  const handlePhotoSelect = async (e: React.ChangeEvent<HTMLInputElement>, photoType: string) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Convert to base64
    const reader = new FileReader();
    reader.onload = () => {
      const base64 = reader.result as string;
      const newPhoto: UploadedPhoto = {
        id: `${photoType}_${Date.now()}`,
        photo_type: photoType,
        image_base64: base64,
        filename: file.name,
      };
      setPhotos(prev => [...prev.filter(p => p.photo_type !== photoType), newPhoto]);
      setActivePhotoType(null);
    };
    reader.readAsDataURL(file);
  };

  const removePhoto = (photoType: string) => {
    setPhotos(prev => prev.filter(p => p.photo_type !== photoType));
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const getPhotoForType = (photoType: string): UploadedPhoto | undefined => {
    return photos.find(p => p.photo_type === photoType);
  };

  // Evidence checkbox handling
  const toggleEvidence = (code: string) => {
    setSelectedEvidence(prev =>
      prev.includes(code)
        ? prev.filter(c => c !== code)
        : [...prev, code]
    );
  };

  // Submit identification
  const handleIdentify = async () => {
    if (!livestockType) {
      setError('Please select the type of livestock affected');
      return;
    }

    if (selectedEvidence.length === 0 && photos.length === 0) {
      setError('Please select at least one evidence item or upload a photo');
      return;
    }

    try {
      setAnalyzing(true);
      setError(null);

      // Extract time and location from evidence
      const timeEvidence = selectedEvidence.find(e => ['daytime', 'nighttime', 'dawn_dusk', 'unknown_time'].includes(e));
      const locationEvidence = selectedEvidence.find(e => ['in_coop', 'in_run', 'free_range', 'near_woods', 'open_field', 'near_water'].includes(e));

      let identifyResult;

      if (photos.length > 0) {
        // Use multi-photo analysis endpoint
        identifyResult = await bridgeClubAPI.identifyPredatorMultiPhoto({
          livestock_type: livestockType,
          photos: photos.map(p => ({
            image_base64: p.image_base64,
            photo_type: p.photo_type,
          })),
          evidence_list: selectedEvidence,
          time_of_attack: timeEvidence || 'unknown',
          context: additionalContext || undefined,
        });
      } else {
        // Evidence-only analysis
        identifyResult = await bridgeClubAPI.identifyPredator({
          livestock_type: livestockType,
          evidence_list: selectedEvidence,
          time_of_attack: timeEvidence || 'unknown',
          location: locationEvidence || '',
          context: additionalContext || undefined,
        });
      }

      setResult(identifyResult);
      setStep(4); // Go to results step
    } catch (err: any) {
      console.error('Identification failed:', err);
      // The identifier is members-only and capped per member (30 an hour, 100 a day)
      // because every photo check is a paid AI call. Say which one happened.
      if (err.status === 429) {
        setError("You've used the identifier a lot in a short time. Please try again in a little while.");
      } else if (err.status === 401 || err.status === 403) {
        setError('Please sign in to your Bridge Club account to use the identifier.');
      } else {
        setError((typeof err.error === 'string' && err.error) || 'Failed to identify predator. Please try again.');
      }
    } finally {
      setAnalyzing(false);
    }
  };

  // Save incident to farm records
  const handleSaveIncident = async () => {
    if (!result?.primary_suspect) return;

    try {
      setSaving(true);
      await bridgeClubAPI.savePredatorIncident({
        predator_type: result.primary_suspect.predator_type,
        livestock_type: livestockType,
        count_lost: countLost,
        evidence_list: selectedEvidence,
        notes: additionalContext,
        photos: photos.map(p => ({
          image_base64: p.image_base64,
          photo_type: p.photo_type,
          filename: p.filename,
        })),
        batch_id: selectedBatchId || undefined,
        zone_id: selectedZoneId || undefined,
      });
      setSaved(true);
    } catch (err: any) {
      setError(err.error || 'Failed to save incident');
    } finally {
      setSaving(false);
    }
  };

  // Reset wizard
  const handleStartOver = () => {
    setStep(1);
    setLivestockType('');
    setSelectedEvidence([]);
    setPhotos([]);
    setActivePhotoType(null);
    setAdditionalContext('');
    setCountLost(1);
    setSelectedBatchId(null);
    setSelectedZoneId(null);
    setResult(null);
    setSaved(false);
    setError(null);
    setShowPhotoDetails(false);
  };

  // Get confidence level display
  const getConfidenceBadge = (level: string) => {
    const badges: Record<string, { label: string; className: string }> = {
      high: { label: 'High Confidence', className: 'confidence-high' },
      medium_high: { label: 'Moderate-High', className: 'confidence-medium-high' },
      medium: { label: 'Moderate', className: 'confidence-medium' },
      low: { label: 'Low Confidence', className: 'confidence-low' },
      very_low: { label: 'Inconclusive', className: 'confidence-very-low' },
    };
    return badges[level] || badges.low;
  };

  // Render step content
  const renderStepContent = () => {
    switch (step) {
      case 1:
        return renderStep1();
      case 2:
        return renderStep2();
      case 3:
        return renderStep3();
      case 4:
        return renderResults();
      default:
        return null;
    }
  };

  // Step 1: Livestock Type & Multi-Photo Upload
  const renderStep1 = () => (
    <div className="pi-step">
      <h3>What type of animal was affected?</h3>

      <div className="pi-livestock-grid">
        {LIVESTOCK_TYPES.map(type => (
          <button
            key={type.id}
            className={`pi-livestock-btn ${livestockType === type.id ? 'selected' : ''}`}
            onClick={() => setLivestockType(type.id)}
          >
            {type.label}
          </button>
        ))}
      </div>

      <div className="pi-photo-section">
        <h4>Upload Photos (Optional but Recommended)</h4>
        <p className="pi-hint">
          Photos help our AI identify wound patterns and attack signatures more accurately.
          Different types of photos provide different clues. The more evidence you capture, the better the identification.
        </p>

        {/* Photo type grid */}
        <div className="pi-photo-types-grid">
          {PHOTO_TYPES.map(photoType => {
            const uploadedPhoto = getPhotoForType(photoType.id);
            const isActive = activePhotoType === photoType.id;

            return (
              <div
                key={photoType.id}
                className={`pi-photo-type-card ${uploadedPhoto ? 'has-photo' : ''} ${isActive ? 'active' : ''}`}
              >
                {uploadedPhoto ? (
                  // Show uploaded photo preview
                  <div className="pi-photo-type-preview">
                    <img src={uploadedPhoto.image_base64} alt={photoType.label} />
                    <div className="pi-photo-type-overlay">
                      <span className="pi-photo-type-check">✓</span>
                      <button
                        className="pi-photo-type-remove"
                        onClick={() => removePhoto(photoType.id)}
                        title="Remove photo"
                      >
                        ✕
                      </button>
                    </div>
                    <div className="pi-photo-type-label">{photoType.label}</div>
                  </div>
                ) : (
                  // Show upload button
                  <button
                    className="pi-photo-type-upload"
                    onClick={() => setActivePhotoType(isActive ? null : photoType.id)}
                  >
                    <span className="pi-photo-type-icon">{photoType.icon}</span>
                    <span className="pi-photo-type-name">{photoType.label}</span>
                    {isActive && (
                      <span className="pi-photo-type-instruction">{photoType.instruction}</span>
                    )}
                  </button>
                )}

                {/* File input for this photo type */}
                {isActive && !uploadedPhoto && (
                  <div className="pi-photo-type-input-wrapper">
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/*"
                      capture="environment"
                      onChange={(e) => handlePhotoSelect(e, photoType.id)}
                      id={`photo-${photoType.id}`}
                      className="pi-photo-type-input"
                    />
                    <label htmlFor={`photo-${photoType.id}`} className="pi-photo-type-capture-btn">
                      📷 Take Photo
                    </label>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {photos.length > 0 && (
          <div className="pi-photos-summary">
            <span className="pi-photos-count">{photos.length} photo{photos.length !== 1 ? 's' : ''} added</span>
            <span className="pi-photos-types">
              ({photos.map(p => PHOTO_TYPES.find(t => t.id === p.photo_type)?.label || p.photo_type).join(', ')})
            </span>
          </div>
        )}
      </div>

      <div className="pi-nav">
        {onClose && (
          <button className="pi-btn pi-btn-secondary" onClick={onClose}>
            Cancel
          </button>
        )}
        <button
          className="pi-btn pi-btn-primary"
          onClick={() => setStep(2)}
          disabled={!livestockType}
        >
          Next: Evidence Checklist
        </button>
      </div>
    </div>
  );

  // Step 2: Evidence Checklist
  const renderStep2 = () => (
    <div className="pi-step">
      <h3>Select all evidence that applies</h3>
      <p className="pi-hint">Check everything you observed - multiple selections help narrow down the predator.</p>

      {checklist && Object.entries(checklist).map(([categoryKey, category]) => (
        <div key={categoryKey} className="pi-checklist-category">
          <h4>{category.label}</h4>
          <div className="pi-checklist-options">
            {category.options.map(option => (
              <label key={option.code} className="pi-checkbox-label">
                <input
                  type="checkbox"
                  checked={selectedEvidence.includes(option.code)}
                  onChange={() => toggleEvidence(option.code)}
                />
                <span className="pi-checkbox-text">{option.label}</span>
              </label>
            ))}
          </div>
        </div>
      ))}

      <div className="pi-nav">
        <button className="pi-btn pi-btn-secondary" onClick={() => setStep(1)}>
          Back
        </button>
        <button
          className="pi-btn pi-btn-primary"
          onClick={() => setStep(3)}
        >
          Next: Additional Details
        </button>
      </div>
    </div>
  );

  // Step 3: Additional Details & Analyze
  const renderStep3 = () => (
    <div className="pi-step">
      <h3>Additional Details</h3>

      <div className="pi-form-group">
        <label>How many animals were lost?</label>
        <input
          type="number"
          min="1"
          value={countLost}
          onChange={(e) => setCountLost(parseInt(e.target.value) || 1)}
          className="pi-input"
        />
      </div>

      {/* Wholesale-specific: Batch and Zone selection */}
      {isWholesale && batches.length > 0 && (
        <div className="pi-form-group">
          <label>Link to Batch (Optional)</label>
          <select
            value={selectedBatchId || ''}
            onChange={(e) => setSelectedBatchId(e.target.value ? parseInt(e.target.value) : null)}
            className="pi-select"
          >
            <option value="">-- Select a batch --</option>
            {batches.map(batch => (
              <option key={batch.id} value={batch.id}>
                {batch.custom_name || `${batch.species} (${batch.count})`}
              </option>
            ))}
          </select>
          <p className="pi-field-hint">Linking to a batch helps track losses per group</p>
        </div>
      )}

      {isWholesale && zones.length > 0 && (
        <div className="pi-form-group">
          <label>Location / Zone (Optional)</label>
          <select
            value={selectedZoneId || ''}
            onChange={(e) => setSelectedZoneId(e.target.value ? parseInt(e.target.value) : null)}
            className="pi-select"
          >
            <option value="">-- Select a zone --</option>
            {zones.map(zone => (
              <option key={zone.id} value={zone.id}>
                {zone.name}
              </option>
            ))}
          </select>
          <p className="pi-field-hint">Track which areas have predator activity</p>
        </div>
      )}

      <div className="pi-form-group">
        <label>Any additional context? (Optional)</label>
        <textarea
          value={additionalContext}
          onChange={(e) => setAdditionalContext(e.target.value)}
          className="pi-textarea"
          placeholder="e.g., 'Happened near the tree line', 'Second attack this week', 'Found feathers by the creek'"
          rows={3}
        />
      </div>

      <div className="pi-summary">
        <h4>Analysis Summary</h4>
        <ul>
          <li><strong>Livestock:</strong> {LIVESTOCK_TYPES.find(t => t.id === livestockType)?.label}</li>
          <li><strong>Photos:</strong> {photos.length > 0 ? `${photos.length} uploaded (${photos.map(p => PHOTO_TYPES.find(t => t.id === p.photo_type)?.label || p.photo_type).join(', ')})` : 'Not provided'}</li>
          <li><strong>Evidence items:</strong> {selectedEvidence.length} selected</li>
        </ul>
      </div>

      {error && <div className="pi-error">{error}</div>}

      <div className="pi-nav">
        <button className="pi-btn pi-btn-secondary" onClick={() => setStep(2)}>
          Back
        </button>
        <button
          className="pi-btn pi-btn-primary pi-btn-analyze"
          onClick={handleIdentify}
          disabled={analyzing}
        >
          {analyzing ? (
            <>
              <span className="pi-spinner"></span>
              Analyzing...
            </>
          ) : (
            'Identify Predator'
          )}
        </button>
      </div>
    </div>
  );

  // Step 4: Results
  const renderResults = () => {
    if (!result) return null;

    const { primary_suspect, combined_results, confidence_level, photo_analysis } = result;
    const confidenceBadge = getConfidenceBadge(confidence_level);

    return (
      <div className="pi-step pi-results">
        <div className="pi-results-header">
          <h3>Analysis Results</h3>
          <span className={`pi-confidence-badge ${confidenceBadge.className}`}>
            {confidenceBadge.label}
          </span>
        </div>

        {/* Primary Suspect */}
        {primary_suspect && (
          <div className="pi-primary-suspect">
            <div className="pi-suspect-header">
              <div className="pi-suspect-header-left">
                {primary_suspect.image_url && (
                  <img
                    src={primary_suspect.image_url}
                    alt={primary_suspect.name}
                    className="pi-suspect-image"
                    onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                  />
                )}
                <div className="pi-suspect-title">
                  <h4>Most Likely Predator</h4>
                  <div className="pi-suspect-name">{primary_suspect.name}</div>
                  <div className="pi-suspect-category">{primary_suspect.category.replace('_', ' ')}</div>
                </div>
              </div>
              <div className="pi-match-badge match-best">Best Match</div>
            </div>

            <div className="pi-evidence-match">
              {getEvidenceMatchDescription(primary_suspect.combined_score)}
            </div>

            {primary_suspect.reasoning && (
              <div className="pi-suspect-reasoning">
                <strong>Why we think this:</strong> {primary_suspect.reasoning}
              </div>
            )}

            {primary_suspect.typical_losses && (
              <div className="pi-suspect-info">
                <strong>Typical pattern:</strong> {primary_suspect.typical_losses}
              </div>
            )}

            {primary_suspect.carcass_condition && (
              <div className="pi-suspect-info">
                <strong>Usually leaves:</strong> {primary_suspect.carcass_condition}
              </div>
            )}

            {primary_suspect.legal_note && (
              <div className="pi-legal-note">
                <span className="pi-legal-icon">⚖️</span>
                {primary_suspect.legal_note}
              </div>
            )}

            {/* Prevention Tips */}
            {primary_suspect.prevention && primary_suspect.prevention.length > 0 && (
              <div className="pi-prevention-section">
                <h5>Prevention Recommendations</h5>
                <ul className="pi-prevention-list">
                  {primary_suspect.prevention.map((item, idx) => (
                    <li key={idx}>
                      {item.term ? (
                        <PreventionTip
                          tip={item.tip}
                          term={item.term}
                          termData={preventionTerms[item.term]}
                        />
                      ) : (
                        item.tip
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}

        {/* Other Possibilities - Expandable Accordion */}
        {combined_results.length > 1 && (
          <div className="pi-other-suspects">
            <h4>Also Consider</h4>
            <p className="pi-other-intro">Click to expand for details and prevention tips</p>
            {combined_results.slice(1, 5).map((suspect, idx) => {
              const matchStrength = getMatchStrength(suspect.combined_score, false);
              const isExpanded = expandedAlternatives.has(idx);

              return (
                <div key={idx} className={`pi-other-suspect-card ${isExpanded ? 'expanded' : ''}`}>
                  <button
                    className="pi-other-suspect-header"
                    onClick={() => toggleAlternativeExpanded(idx)}
                    aria-expanded={isExpanded}
                  >
                    <div className="pi-other-suspect-left">
                      {suspect.image_url && (
                        <img
                          src={suspect.image_url}
                          alt={suspect.name}
                          className="pi-other-suspect-thumb"
                          onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                        />
                      )}
                      <span className="pi-other-name">{suspect.name}</span>
                    </div>
                    <div className="pi-other-suspect-right">
                      <span className={`pi-match-badge ${matchStrength.className}`}>
                        {matchStrength.label}
                      </span>
                      <span className={`pi-expand-icon ${isExpanded ? 'expanded' : ''}`}>
                        ▼
                      </span>
                    </div>
                  </button>

                  {isExpanded && (
                    <div className="pi-other-suspect-content">
                      {suspect.typical_losses && (
                        <div className="pi-suspect-info">
                          <strong>Typical pattern:</strong> {suspect.typical_losses}
                        </div>
                      )}
                      {suspect.carcass_condition && (
                        <div className="pi-suspect-info">
                          <strong>Usually leaves:</strong> {suspect.carcass_condition}
                        </div>
                      )}
                      {suspect.legal_note && (
                        <div className="pi-legal-note">
                          <span className="pi-legal-icon">⚖️</span>
                          {suspect.legal_note}
                        </div>
                      )}
                      {suspect.prevention && suspect.prevention.length > 0 && (
                        <div className="pi-prevention-section">
                          <h5>Prevention for {suspect.name}</h5>
                          <ul className="pi-prevention-list">
                            {suspect.prevention.slice(0, 4).map((item, pIdx) => (
                              <li key={pIdx}>
                                {item.term ? (
                                  <PreventionTip
                                    tip={item.tip}
                                    term={item.term}
                                    termData={preventionTerms[item.term]}
                                  />
                                ) : (
                                  item.tip
                                )}
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* Photo Analysis Details */}
        {photo_analysis && photo_analysis.wounds_observed && photo_analysis.wounds_observed.length > 0 && (
          <div className="pi-photo-analysis">
            <h4>Photo Analysis Details</h4>
            <div className="pi-wounds-list">
              {photo_analysis.wounds_observed.map((wound, idx) => (
                <div key={idx} className="pi-wound-item">
                  <strong>{wound.location}:</strong> {wound.type} - {wound.description}
                </div>
              ))}
            </div>
            {photo_analysis.photo_quality_notes && (
              <p className="pi-photo-notes">Note: {photo_analysis.photo_quality_notes}</p>
            )}
          </div>
        )}

        {/* Save Incident */}
        {!saved ? (
          <div className="pi-save-section">
            <p>Would you like to save this incident to your farm records?</p>
            <button
              className="pi-btn pi-btn-secondary"
              onClick={handleSaveIncident}
              disabled={saving}
            >
              {saving ? 'Saving...' : 'Save to Farm Records'}
            </button>
          </div>
        ) : (
          <div className="pi-saved-message">
            Incident saved to your farm records
          </div>
        )}

        <div className="pi-nav">
          <button className="pi-btn pi-btn-secondary" onClick={handleStartOver}>
            Start Over
          </button>
          {onClose && (
            <button className="pi-btn pi-btn-primary" onClick={onClose}>
              Done
            </button>
          )}
        </div>
      </div>
    );
  };

  // Loading state
  if (loading) {
    return (
      <div className={`pi-container ${isModal ? 'pi-modal' : ''}`}>
        <div className="pi-loading">
          <span className="pi-spinner"></span>
          Loading predator identification tool...
        </div>
      </div>
    );
  }

  return (
    <div className={`pi-container ${isModal ? 'pi-modal' : ''}`}>
      <div className="pi-header">
        <h2>What Killed My Animal?</h2>
        <p>Identify predators and learn prevention strategies</p>
      </div>

      {/* Progress Steps */}
      <div className="pi-progress">
        {[1, 2, 3, 4].map(s => (
          <div
            key={s}
            className={`pi-progress-step ${step >= s ? 'active' : ''} ${step === s ? 'current' : ''}`}
          >
            <span className="pi-step-number">{s}</span>
            <span className="pi-step-label">
              {s === 1 && 'Livestock'}
              {s === 2 && 'Evidence'}
              {s === 3 && 'Details'}
              {s === 4 && 'Results'}
            </span>
          </div>
        ))}
      </div>

      {error && step !== 3 && step !== 4 && (
        <div className="pi-error">{error}</div>
      )}

      {renderStepContent()}
    </div>
  );
};

export default PredatorIdentifier;
